import { type CSSProperties, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../ui/Icon'
import Avatar from '../../../../ui/Avatar'

import {
  SKILL_LEVEL_VALUES,
  SkillLevel,
} from '../../../../../types/user/userProfile'
import { getSkillLevelCategory } from '../../../../../utils/profile/profile'

export type UserMenuItem = {
  path: string
  label: string
  icon: string
  meta?: string
}

type UserMenuProps = {
  name: string
  fullName: string
  email?: string
  picture?: string | null
  skillLevel?: SkillLevel | null
  items: UserMenuItem[]
  onNavigate: (path: string) => void
  onSignOut: () => void
}

const SKILL_LEVELS = Object.values(SkillLevel)

export const UserMenu = ({
  name,
  fullName,
  email,
  picture,
  skillLevel,
  items,
  onNavigate,
  onSignOut,
}: UserMenuProps) => {
  const { t } = useTranslation(['nav', 'account'])
  const [open, setOpen] = useState(false)
  const [triggerWidth, setTriggerWidth] = useState(0)
  const menuRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    document.addEventListener('keydown', handleEscape)

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
      document.removeEventListener('keydown', handleEscape)
    }
  }, [open])

  const close = () => setOpen(false)

  const toggle = () => {
    setTriggerWidth(triggerRef.current?.offsetWidth ?? 0)
    setOpen(value => !value)
  }

  const select = (action: () => void) => {
    close()
    action()
  }

  const level = skillLevel ? SKILL_LEVEL_VALUES[skillLevel] : null
  const levelLabel = level
    ? t('account:account.profile.skill-level.value', { level })
    : undefined
  const levelProgress = skillLevel
    ? ((SKILL_LEVELS.indexOf(skillLevel) + 1) / SKILL_LEVELS.length) * 100
    : 0

  return (
    <div
      ref={menuRef}
      className={`ph-user-menu d-lg-block${open ? ' open' : ''}`}
    >
      <button
        ref={triggerRef}
        type="button"
        className="ph-user-menu-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={toggle}
      >
        <span className="ph-user-menu-avatar">
          <Avatar name={name} image={picture} />
        </span>
        <span className="ph-user-menu-identity">
          <span className="name">{name}</span>
          {levelLabel && <span className="sub">{levelLabel}</span>}
        </span>
        <Icon name="chev" size={14} />
      </button>

      {open && (
        <div
          className="ph-user-menu-panel"
          role="menu"
          style={
            {
              '--ph-user-menu-trigger-width': `${triggerWidth}px`,
            } as CSSProperties
          }
        >
          <div className="ph-user-menu-profile">
            <button
              type="button"
              className="ph-user-menu-close"
              aria-label={t('top-bar.close-menu')}
              onClick={close}
            >
              <Icon name="x" size={14} />
            </button>

            <div className="ph-user-menu-photo">
              <Avatar name={fullName} image={picture} />
            </div>

            <div className="name">{fullName}</div>
            {email && <div className="email">{email}</div>}

            {skillLevel && level && (
              <div className="ph-user-menu-level">
                <div className="ph-user-menu-level-head">
                  <span className="badge">
                    {t('user-menu.level-badge', { level })}
                  </span>
                  <span className="category">
                    {t(
                      `user-menu.level-category.${getSkillLevelCategory(skillLevel)}`
                    )}
                  </span>
                </div>
                <div className="ph-user-menu-level-bar">
                  <span style={{ width: `${levelProgress}%` }} />
                </div>
                <div className="ph-user-menu-level-scale">
                  <span>{SKILL_LEVEL_VALUES[SKILL_LEVELS[0]]}</span>
                  <span>{t('user-menu.level-range')}</span>
                  <span>
                    {SKILL_LEVEL_VALUES[SKILL_LEVELS[SKILL_LEVELS.length - 1]]}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="ph-user-menu-section">
            {items.map(({ path, label, icon, meta }) => (
              <button
                key={path}
                type="button"
                role="menuitem"
                className="ph-user-menu-item"
                onClick={() => select(() => onNavigate(path))}
              >
                <span className="tile">
                  <Icon name={icon} size={16} />
                </span>
                <span className="label">{label}</span>
                {meta && <span className="meta">{meta}</span>}
              </button>
            ))}
          </div>

          <div className="ph-user-menu-section">
            <button
              type="button"
              role="menuitem"
              className="ph-user-menu-item danger"
              onClick={() => select(onSignOut)}
            >
              <span className="tile">
                <Icon name="logout" size={16} />
              </span>
              <span className="label">{t('top-bar.sign-out')}</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
